import {
  SignOutIcon,
  ListIcon,
  XIcon,
  SidebarSimpleIcon,
} from "@phosphor-icons/react";
import { Sider } from "@refinedev/antd";
import {
  ITreeMenu,
  useIsAuthenticated,
  useIsExistAuthentication,
  useLogout,
  useMenu,
  useTranslate,
  useWarnAboutChange,
} from "@refinedev/core";
import { Button, ConfigProvider, Flex, theme } from "antd";
import type { GlobalToken } from "antd";
import {
  LibertifyIso,
  LibertifyLogo,
  Sidebar,
  SidebarDivider,
  SidebarItem,
  SidebarSection,
  colors,
  type SidebarSectionTone,
} from "@libertify/ui";
import { OrganizationSwitch } from "components/OrganizationSwitch";
import { ThemeSwitch } from "components/ThemeSwitch";
import { UserMenu } from "components/UserMenu";
import { WorkspaceSwitch } from "components/WorkspaceSwitch";
import { useAntTheme } from "hooks/useAntTheme";
import { useSession } from "hooks/useSession";
import { useAuthActions } from "hooks/useAuthActions";
import useIsAdmin from "hooks/useIsAdmin";
import { useBoundStore } from "@/store";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { MouseEvent, ReactNode } from "react";
import { Link, useMatch, type LinkProps } from "react-router-dom";
import styled from "styled-components";
import {
  MOBILE_HEADER_HEIGHT,
  NAV_TEXT_COLOR,
  SIDER_COLLAPSED_WIDTH,
  SIDER_PRESET,
  SIDER_WIDTH,
  sidebarMenuItemStyle,
} from "./styles";
import { FeatureFlags } from "@/constants";
import { getAppTitle } from "helpers/utils/";
import { useGetActiveSubscriptions } from "pages/pricing/hooks/useGetActiveSubscriptions/";
import { UpgradeButton } from "components/UpgradeButton";
import { useDebouncedCallback } from "use-debounce";
import { useFeatureFlag } from "hooks/useFeature/";
import { createAccessControlProvider } from "config/accessControl";

const { useToken } = theme;

// Brand spec: the iso is 24px tall in the sidebar. `LibertifyLogo` already
// renders it at 24px by default; the collapsed iso-only mark matches it.
const SIDEBAR_ISO_HEIGHT = 24;
// Same size as the lib SidebarItem icons.
const SIDEBAR_ICON_SIZE = 24;

type SidebarCategory = "navigation" | "customFeatures";

// Render order + section titles. Add a new entry here (and to SIDEBAR_CATEGORIES
// below) to introduce another section. The main navigation has no title; the
// custom features block is muted and fills the space down to the footer.
const SIDEBAR_SECTIONS: ReadonlyArray<{
  category: SidebarCategory;
  titleKey?: string;
  tone?: SidebarSectionTone;
}> = [
  { category: "navigation" },
  {
    category: "customFeatures",
    titleKey: "src.App.sidebar.customFeatures",
    tone: "muted",
  },
];

// Maps each resource name to the sidebar section it should render in.
// Items not listed default to "navigation" so a newly added resource is
// still visible (and the team will notice and recategorize as needed).
const SIDEBAR_CATEGORIES: Record<string, SidebarCategory> = {
  // Navigation
  media_home: "navigation",
  workspaces: "navigation",
  media_projects: "navigation",
  media_ctas: "navigation",

  // Custom Features
  users: "customFeatures",
  manage_subscriptions: "customFeatures",
  media_asset_libraries: "customFeatures",
  media_dashboard: "customFeatures",
  media_templates: "customFeatures",
  media_contacts: "customFeatures",
  media_voices: "customFeatures",
  media_brandkits: "customFeatures",
  assets: "customFeatures",
  media_products: "customFeatures",
  media_faces: "customFeatures",
  media_presenters: "customFeatures",
  media_pronunciations: "customFeatures",
  media_theme_editor: "customFeatures",
  media_media: "customFeatures",
  media_batches: "customFeatures",
  media_assets: "customFeatures",
  media_assets_review: "customFeatures",
  feature_flags: "customFeatures",
};

type SidebarNavItem = {
  key: string;
  name: string;
  label: string;
  icon?: ReactNode;
  route: string;
  active: boolean;
};

type SidebarCollapseToggleProps = {
  collapsed: boolean;
  onClick: () => void;
};

const SidebarCollapseToggle = ({
  collapsed,
  onClick,
}: SidebarCollapseToggleProps) => {
  const t = useTranslate();
  return (
    <Button
      type="text"
      onClick={onClick}
      aria-label={
        collapsed ? t("buttons.expandSidebar") : t("buttons.collapseSidebar")
      }
      style={{ width: collapsed ? "100%" : "auto", padding: 0 }}
    >
      <SidebarSimpleIcon size={SIDEBAR_ICON_SIZE} color={NAV_TEXT_COLOR} />
    </Button>
  );
};

/**
 * A sidebar nav link that honors Refine's unsaved-changes guard. React-router's
 * raw `<Link>` navigates via the History API and bypasses
 * `warnWhenUnsavedChanges` (which only wraps Refine's own navigation), so we
 * confirm here before a plain in-app navigation discards unsaved work — mirroring
 * the manual guard already used for logout. Modified / new-tab / non-primary
 * clicks pass through untouched (they don't navigate the current page away).
 */
const GuardedNavLink = ({ onClick, ...props }: LinkProps) => {
  const t = useTranslate();
  const { warnWhen, setWarnWhen } = useWarnAboutChange();

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    const isModifiedClick =
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      event.button !== 0;
    if (warnWhen && !isModifiedClick) {
      if (!globalThis.confirm(t("warnWhenUnsavedChanges"))) {
        event.preventDefault();
        return;
      }
      setWarnWhen(false);
    }
    onClick?.(event);
  };

  return <Link {...props} onClick={handleClick} />;
};

export const CustomSider: typeof Sider = () => {
  const t = useTranslate();
  const { token } = useToken();
  const isDarkModeEnabled = useFeatureFlag(FeatureFlags.DarkMode);
  const isThemeEditorEnabled = useFeatureFlag(FeatureFlags.ThemeEditor);
  const enabledFeatureFlags = useMemo(
    () => ({
      [FeatureFlags.ThemeEditor]: isThemeEditorEnabled,
    }),
    [isThemeEditorEnabled]
  );
  const { hasAccesses, user, accesses } = useSession();
  const { setSidebarCollapsed, setSidebarHovered } = useAuthActions();
  const collapsed = useBoundStore((state) => state.sidebarCollapsed);

  const isExistAuthentication = useIsExistAuthentication();
  const { data: authenticated } = useIsAuthenticated();
  const isAuthenticated = authenticated?.authenticated;
  const { warnWhen, setWarnWhen } = useWarnAboutChange();
  const { mutate: mutateLogout } = useLogout();
  const { menuItems, selectedKey } = useMenu();
  const isOnboarding = useMatch("/onboarding");
  const isRegister = useMatch("/register");
  const isVerification = useMatch("/verification");
  const isLogin = useMatch("/login");
  const { isMobile, orientation } = useAntTheme();
  const isMobileLandscape = isMobile && orientation === "landscape";
  const { activeSubscriptionDetails, isFreeUser } = useGetActiveSubscriptions();
  const { plan, isCustom } = activeSubscriptionDetails || {};
  const [isHoverExpanded, setIsHoverExpanded] = useState(false);

  const toggle = (next: boolean) => setSidebarCollapsed(next);

  // State to hold which resources the user can access
  const [allowedResources, setAllowedResources] = useState<
    Record<string, boolean>
  >({});

  // Helper to get all resource names from the menu tree
  const getResourceNames = (tree: ITreeMenu[]): string[] => {
    return tree.flatMap((item) => {
      if (item.children.length > 0) {
        return getResourceNames(item.children);
      }
      return [item.name];
    });
  };

  // Memoize resource names to prevent infinite loops
  const resourceNames = useMemo(
    () => getResourceNames(menuItems),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [menuItems.length]
  );

  // Reset allowedResources when user changes to force recalculation
  useEffect(() => {
    setAllowedResources({});
  }, [user?.id]);

  // Memoize accessControlProvider to prevent recreating on every render
  const accessControlProvider = useMemo(() => {
    if (!hasAccesses) return null;
    return createAccessControlProvider(accesses, hasAccesses);
  }, [accesses, hasAccesses]);

  // Check access for all menu items using the memoized accessControlProvider
  // This ensures scope validation matches what happens on refresh
  useEffect(() => {
    if (!accessControlProvider || resourceNames.length === 0) return;
    let isCancelled = false;

    // Check access for each resource asynchronously
    const checkAllAccess = async () => {
      const accessMap: Record<string, boolean> = {};
      await Promise.all(
        resourceNames.map(async (resource) => {
          try {
            const result = await accessControlProvider.can({
              resource,
              action: "list",
            });
            accessMap[resource] = result.can;
          } catch (error) {
            console.error(
              "[Sider] Error checking access for resource:",
              resource,
              error
            );
            accessMap[resource] = false;
          }
        })
      );

      // Only update state if effect hasn't been cancelled
      if (!isCancelled) {
        setAllowedResources(accessMap);
      }
    };

    checkAllAccess().catch((error) => {
      console.error("[Sider] Failed to check access:", error);
    });

    // Cleanup function to prevent state updates after unmount
    return () => {
      isCancelled = true;
    };
  }, [accessControlProvider, resourceNames, user?.id]);

  const isAdmin = useIsAdmin();

  // Lock background scroll while the sidebar drawer is open on mobile.
  useEffect(() => {
    const lock = isMobile && collapsed === false;
    document.body.style.overflow = lock ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobile, collapsed]);

  const buildMenuItems = useCallback(
    (tree: ITreeMenu[], selectedKey: string): SidebarNavItem[] => {
      return tree.flatMap((item: ITreeMenu) => {
        const { name, children, meta, key, list } = item;

        // Filter out items that are hidden via meta.hide, adminOnly, or a disabled featureFlag
        const isFeatureFlagDisabled =
          meta?.featureFlag !== undefined &&
          !enabledFeatureFlags[
            meta.featureFlag as keyof typeof enabledFeatureFlags
          ];
        const isHidden =
          !!meta?.hide ||
          (meta?.adminOnly && !isAdmin) ||
          isFeatureFlagDisabled;
        if (isHidden) {
          return [];
        }

        // Filter out items without access (using Refine's accessControlProvider)
        if (!allowedResources[name] && children.length === 0) {
          return [];
        }

        const icon = meta?.icon;
        if (
          process.env.NODE_ENV !== "production" &&
          meta?.label !== undefined &&
          typeof meta.label !== "string"
        ) {
          console.warn(
            `[Sider] Non-string meta.label for resource "${name}"; falling back to resource name.`
          );
        }
        const label = typeof meta?.label === "string" ? meta.label : name;
        let route: string | undefined;
        if (typeof list === "string") {
          route = list;
        } else if (typeof list === "function") {
          route = key;
        } else {
          route = list?.path;
        }

        if (children.length > 0) {
          return buildMenuItems(children, selectedKey);
        }

        // Lib SidebarItem expects a route for `as={Link} to={...}`; items with no
        // route are dropped
        if (!route) {
          return [];
        }

        const isSelected = route === selectedKey || key === selectedKey;

        return [
          {
            key: route || key || name,
            name,
            label,
            icon,
            route,
            active: isSelected,
          },
        ];
      });
    },
    [allowedResources, isAdmin, enabledFeatureFlags]
  );

  // All cleanup (token, cache, storage, Zustand) is handled by authProvider.logout()
  // triggered by mutateLogout(). Single path prevents duplicate cleanup and drift.
  const handleLogout = () => {
    let confirm = true;
    if (warnWhen) {
      confirm = globalThis.confirm(t("warnWhenUnsavedChanges"));
    }
    if (confirm) {
      setWarnWhen(false);
      mutateLogout();
    }
  };

  const logout = isExistAuthentication && isAuthenticated && (
    <Button
      block
      type="text"
      onClick={handleLogout}
      style={sidebarMenuItemStyle}
      icon={<SignOutIcon size={24} />}
    >
      {t("buttons.logout", "Logout")}
    </Button>
  );
  const isCreateOrganization = useMatch("/organization/create");
  const menuItemsData = useMemo(() => {
    const hasAccess =
      isAuthenticated && hasAccesses && !isOnboarding && !isCreateOrganization;
    const menu = hasAccess ? menuItems : [];

    return buildMenuItems(menu, selectedKey);
  }, [
    isAuthenticated,
    hasAccesses,
    isOnboarding,
    isCreateOrganization,
    menuItems,
    selectedKey,
    buildMenuItems,
  ]);

  const groupedMenuItems = useMemo(() => {
    const navigation: SidebarNavItem[] = [];
    const customFeatures: SidebarNavItem[] = [];
    menuItemsData.forEach((item) => {
      const category = SIDEBAR_CATEGORIES[item.name] ?? "navigation";
      if (category === "customFeatures") {
        customFeatures.push(item);
      } else {
        navigation.push(item);
      }
    });
    return { navigation, customFeatures };
  }, [menuItemsData]);

  const debouncedMouseEnter = useDebouncedCallback(() => {
    if (!collapsed || isHoverExpanded) return;
    setIsHoverExpanded(true);
    setSidebarHovered(true);
    setSidebarCollapsed(false);
  }, 100);

  const handleMouseLeave = () => {
    // Cancel any pending expansion
    debouncedMouseEnter.cancel();

    if (isHoverExpanded) {
      setSidebarCollapsed(true);
      setIsHoverExpanded(false);
      setSidebarHovered(false);
    }
  };

  const handleCollapse = () => {
    setIsHoverExpanded(false);
    setSidebarHovered(false);
    setSidebarCollapsed(!collapsed);
  };

  const appTitle = useMemo(() => getAppTitle(), []);
  if (isRegister || isVerification || collapsed === null || isLogin) {
    return null;
  }

  const header = !isMobile && (
    <SidebarHeaderRow $collapsed={collapsed}>
      <SidebarLogoLink to="/">
        {collapsed ? (
          <LibertifyIso height={SIDEBAR_ISO_HEIGHT} title={appTitle} />
        ) : (
          <LibertifyLogo title={appTitle} />
        )}
      </SidebarLogoLink>
      {!collapsed && (
        <SidebarCollapseToggle collapsed={collapsed} onClick={handleCollapse} />
      )}
    </SidebarHeaderRow>
  );

  const topContent = isAuthenticated && (
    <>
      <OrgWorkspaceBlock $hidden={collapsed} $token={token}>
        <OrganizationSwitch />
        <WorkspaceSwitch />
      </OrgWorkspaceBlock>
      {!isMobile && collapsed && (
        <SidebarCollapseToggle collapsed={collapsed} onClick={handleCollapse} />
      )}
    </>
  );

  const footer = isAuthenticated && (
    <>
      {isFreeUser && (
        <Flex justify="center" style={{ padding: "24px 0px" }}>
          <UpgradeButton iconOnly={collapsed} buttonLocation="sidebar" />
        </Flex>
      )}
      {user && (
        <>
          {/* Top gap lives on the UpgradeButton wrapper's bottom padding; the
              UserMenu row owns its own vertical padding. */}
          <SidebarDivider />
          <UserMenu
            collapsed={collapsed}
            user={user}
            logout={logout}
            isMobile={isMobile}
            toggleSidebar={toggle}
            planName={plan}
            isCustom={isCustom}
          />
        </>
      )}
    </>
  );

  const desktopSpacerWidth =
    collapsed || isHoverExpanded ? SIDER_COLLAPSED_WIDTH : SIDER_WIDTH;

  return (
    <>
      {isMobile && (
        <MobileHeader
          $bgColor={token.colorBgContainer}
          $borderColor={token.colorBgElevated}
        >
          <SidebarLogoLink to="/" style={{ marginLeft: 4 }}>
            <LibertifyLogo title={appTitle} />
          </SidebarLogoLink>
          <Button
            type="text"
            onClick={() => setSidebarCollapsed(!collapsed)}
            style={{ padding: 4 }}
            aria-label={t("buttons.toggleMenu")}
          >
            {collapsed ? (
              <ListIcon size={24} style={{ color: token.colorText }} />
            ) : (
              <XIcon size={24} style={{ color: token.colorText }} />
            )}
          </Button>
        </MobileHeader>
      )}
      {isMobile && !collapsed && (
        <MobileOverlay onClick={() => setSidebarCollapsed(true)} />
      )}
      <SidebarSpacer
        $width={isMobile ? 0 : desktopSpacerWidth}
        $hidden={isMobile && collapsed}
      >
        <SidebarFloat
          $isMobile={isMobile}
          $isMobileLandscape={isMobileLandscape}
          $hovered={isHoverExpanded}
          /*
           * onMouseLeave fires when the cursor leaves the entire sidebar
           * (including header / footer). onMouseEnter is scoped to the nav
           * area only — see NavHoverZone below — so passing the cursor
           * over the logo or user menu doesn't trigger a hover-expand.
           * Mirrors the original AntdLayout.Sider + ScrollableMenuContainer
           * split. Both skipped on mobile (touch devices fire mouseenter
           * on tap).
           */
          onMouseLeave={isMobile ? undefined : handleMouseLeave}
        >
          <ConfigProvider theme={{ token: { colorText: NAV_TEXT_COLOR } }}>
            <Sidebar
              width={SIDER_PRESET}
              collapsed={collapsed}
              onCollapseChange={setSidebarCollapsed}
              header={header}
              topContent={topContent}
              footer={footer}
            >
              <NavHoverZone
                onMouseEnter={isMobile ? undefined : debouncedMouseEnter}
              >
                {/* Empty sections are skipped so a muted block never shows
                    a lone title. */}
                {SIDEBAR_SECTIONS.filter(
                  ({ category }) => groupedMenuItems[category].length > 0
                ).map(({ category, titleKey, tone }) => (
                  <SidebarSection
                    key={category}
                    title={titleKey ? t(titleKey) : undefined}
                    tone={tone}
                  >
                    {groupedMenuItems[category].map((item) => (
                      <SidebarItem
                        key={item.key}
                        as={GuardedNavLink}
                        to={item.route}
                        label={item.label}
                        icon={item.icon}
                        active={item.active}
                      />
                    ))}
                  </SidebarSection>
                ))}
                {isDarkModeEnabled && !isMobile && (
                  <ThemeSwitch collapsed={collapsed} />
                )}
              </NavHoverZone>
            </Sidebar>
          </ConfigProvider>
        </SidebarFloat>
      </SidebarSpacer>
    </>
  );
};

const SidebarSpacer = styled.div<{ $width: number; $hidden: boolean }>`
  position: relative;
  flex-shrink: 0;
  height: 100vh;
  width: ${({ $width }) => $width}px;
  display: ${({ $hidden }) => ($hidden ? "none" : "block")};
  transition: width 0.2s ease-in-out;
`;

const SidebarFloat = styled.div<{
  $isMobile: boolean;
  $isMobileLandscape: boolean;
  $hovered: boolean;
}>`
  position: fixed;
  ${({ $isMobile }) =>
    $isMobile
      ? `
    right: 0;
    top: ${MOBILE_HEADER_HEIGHT}px;
    bottom: 0;
    z-index: 999;
    width: 80vw;
    min-width: ${SIDER_WIDTH}px;
    max-width: 400px;
    & > aside {
      width: 100%;
      min-width: 0;
    }
  `
      : `
    left: 0;
    top: 0;
    height: 100vh;
  `}
  ${({ $isMobileLandscape }) =>
    $isMobileLandscape &&
    `
    /* Unpin the footer in mobile landscape*/
    & > aside {
      overflow-y: auto;
    }
    & > aside > div:nth-last-child(2) {
      flex: 0 0 auto;
      overflow: visible;
    }
    & > aside > div:last-child {
      margin-top: 0;
    }
  `}
  ${({ $hovered }) =>
    $hovered &&
    `
    z-index: 1000;
    box-shadow: 2px 0 8px rgba(0, 0, 0, 0.08);
  `}
`;

const SidebarHeaderRow = styled.div<{ $collapsed: boolean }>`
  display: flex;
  align-items: center;
  justify-content: ${({ $collapsed }) =>
    $collapsed ? "center" : "space-between"};
  width: 100%;
`;

const SidebarLogoLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  border-radius: 4px;

  &:focus-visible {
    outline: 2px solid ${colors.primary[500]};
    outline-offset: 2px;
  }
`;

// Same horizontal inset as the lib's sidebar rows (header, items, footer). No
// bottom padding: the lib content area's top inset spaces the first item.
const OrgWorkspaceBlock = styled.div<{ $hidden: boolean; $token: GlobalToken }>`
  display: ${({ $hidden }) => ($hidden ? "none" : "flex")};
  flex-direction: column;
  gap: ${({ $token }) => $token.paddingXS}px;
  padding: ${({ $token }) => `${$token.padding}px ${$token.paddingLG}px 0`};
`;

/*
 * Wraps the nav sections inside the lib <Sidebar>'s content area. Used to
 * scope the hover-expand trigger to the nav-items area only — mirroring
 * the original ScrollableMenuContainer's onMouseEnter scope, so that
 * passing the cursor over the header logo or footer (UserMenu /
 * UpgradeButton) doesn't accidentally fire a hover-expand.
 */
const NavHoverZone = styled.div`
  /* Pass the lib content column through, so the muted last section can grow
     down to the footer. */
  display: flex;
  flex-direction: column;
  flex: 1 0 auto;
`;

const MobileHeader = styled.div<{ $bgColor: string; $borderColor: string }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: ${MOBILE_HEADER_HEIGHT}px;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 12px;
  background: ${({ $bgColor }) => $bgColor};
  border-bottom: 1px solid ${({ $borderColor }) => $borderColor};
`;

const MobileOverlay = styled.div`
  position: fixed;
  inset: ${MOBILE_HEADER_HEIGHT}px 0 0;
  z-index: 998;
  background: rgba(0, 0, 0, 0.45);
`;

export const MobileHeaderSpacer: React.FC = () => {
  const { isMobile } = useAntTheme();
  if (!isMobile) return null;
  return <div style={{ height: MOBILE_HEADER_HEIGHT }} />;
};
